import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import Switch from '../../../../molecules/Switch'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import InputColorPicker from '../../../../molecules/InputColorPicker'
import CommaSeparatedField from '../shared/CommaSeparatedField'
import presets from './presets'

const presetOptions = [
  { label: 'Select a preset...', value: '' },
  ...Object.entries(presets).map(([key, preset]) => ({
    label: preset.displayName,
    value: key
  }))
]

const scopeOptions = [
  { label: 'Client — tasks for the current client\'s Redtail family', value: 'client' },
  { label: 'My Book — all tasks for my Redtail user', value: 'user' }
]

const styleVariantOptions = [
  { label: 'Default (Medium)', value: 'default' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function RedtailMetricConfigEditor ({ fieldPrefix }) {
  const { control, watch } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  const presetValue = watch(`${configPrefix}.preset`)
  const hideTitleValue = watch(`${configPrefix}.hideTitle`)
  const hideLabelValue = watch(`${configPrefix}.hideLabel`)
  const hideIconValue = watch(`${configPrefix}.hideIcon`)

  const selectedPreset = useMemo(() => {
    return presetValue ? presets[presetValue] : null
  }, [presetValue])

  return (
    <Grid container spacing={2}>
      {/* ── Core ─────────────────────────────────────── */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Core</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.preset`}
          control={control}
          render={({ field }) => (
            <SydLabel required label='Preset' helperText='Select the metric type this component displays'>
              <SydSelect
                options={presetOptions}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.scope`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Scope' helperText='Filter tasks to a specific client or across your full book'>
              <SydSelect
                options={scopeOptions}
                value={field.value || 'client'}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* ── Appearance ───────────────────────────────── */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>
      {/* Row 1: Title group — Show Title toggle | Title */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideTitle`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show title' helperText='Toggle off to hide the title entirely'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideTitleValue && (
            <Grid item xs={10}>
              <Controller
                name={`${configPrefix}.title`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Title' helperText='Override the preset default title'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <SydInput
                        {...field}
                        value={field.value || ''}
                        placeholder={selectedPreset?.defaults?.title || 'Title'}
                        minWidth='0'
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      {/* Row 2: Icon group — Show Icon toggle | Icon picker | Icon Color */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideIcon`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show title icon' helperText='Toggle off to hide the icon entirely'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideIconValue && (
            <Grid item xs={5}>
              <Controller
                name={`${configPrefix}.icon`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Icon' helperText='Icon name (e.g. redtailSmall)'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <IconPickerField
                        value={field.value || selectedPreset?.defaults?.icon || null}
                        onChange={field.onChange}
                        placeholder={selectedPreset?.defaults?.icon || 'Select an icon...'}
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
          {!hideIconValue && (
            <Grid item xs={5}>
              <Controller
                name={`${configPrefix}.iconColor`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Icon color' helperText='Hex (#fff, #ffffff, #ffffffaa) or rgba(r,g,b,a). Defaults to primary color when blank.'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <InputColorPicker
                        value={field.value || ''}
                        onChange={(v) => field.onChange(v || null)}
                        placeholder='#3D7DDD or rgba(61,125,221,1)'
                        style={{ width: '100%' }}
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      {/* Row 3: Label group — Show Label toggle | Label */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideLabel`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show label' helperText='Toggle off to hide the label entirely'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideLabelValue && (
            <Grid item xs={10}>
              <Controller
                name={`${configPrefix}.label`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Label' helperText='Subtitle shown below the value'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <SydInput
                        {...field}
                        value={field.value || ''}
                        placeholder={selectedPreset?.defaults?.label || 'Label'}
                        minWidth='0'
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      {/* Row 4: Value group — Value Format | Value Color */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Controller
              name={`${configPrefix}.format`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Value Format' helperText='numeral.js format (e.g. 0,0)'>
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <SydInput
                      {...field}
                      value={field.value || ''}
                      placeholder={selectedPreset?.defaults?.format || '0,0'}
                      minWidth='0'
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name={`${configPrefix}.valueColor`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Value color' helperText='Hex (#fff, #ffffff, #ffffffaa) or rgba(r,g,b,a). Inherits when blank.'>
                  <div style={{ width: '100%', minWidth: 0 }}>
                    <InputColorPicker
                      value={field.value || ''}
                      onChange={(v) => field.onChange(v || null)}
                      placeholder='#3D7DDD or rgba(61,125,221,1)'
                      style={{ width: '100%' }}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      {/* Row 5: Style Variant */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Controller
              name={`${fieldPrefix}.styleVariant`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Style Variant' helperText='Visual presentation style'>
                  <SydSelect
                    options={styleVariantOptions}
                    value={field.value || 'default'}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </SydLabel>
              )}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* ── Capabilities + Filtering (side-by-side) ─── */}
      <Grid item xs={12}>
        <div style={{ display: 'flex', gap: '32px' }}>
          <div style={{ width: '220px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={sectionHeading}>Capabilities</div>
            <Controller
              name={`${configPrefix}.enableEdit`}
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                  <span style={{ fontSize: 'var(--font-size-sm)' }}>Allow editing</span>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </div>
              )}
            />
            <Controller
              name={`${configPrefix}.formMode`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Form Mode' helperText='Calendar adds duration, all-day, and recurrence'>
                  <SydSelect
                    minWidth={0}
                    options={[
                      { label: 'Calendar', value: 'calendar' },
                      { label: 'Simple (date only)', value: 'simple' }
                    ]}
                    value={field.value || 'simple'}
                    onChange={(e) => field.onChange(e.target.value || null)}
                  />
                </SydLabel>
              )}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sectionHeading}>Filtering</div>
            <Grid container spacing={2} style={{ marginTop: '4px' }}>
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.categoryIds`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Activity Categories' helperText='Comma-separated category IDs (leave blank for all)'>
                      <CommaSeparatedField
                        value={field.value}
                        onChange={field.onChange}
                        parseAsInt
                        placeholder='e.g. 1, 3, 5'
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.dueWithinDays`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Due Within (days)' helperText='Days to look ahead (default 7)'>
                      <SydInput
                        type='number'
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value
                          if (!raw || !raw.trim()) { field.onChange(null); return }
                          const parsed = parseInt(raw, 10)
                          field.onChange(isNaN(parsed) ? null : parsed)
                        }}
                        placeholder='7'
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name={`${configPrefix}.priorityFilter`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Priority Filter' helperText='Comma-separated priority values from Redtail (e.g. Low, Normal, High or 1, 2, 3). Leave blank for all.'>
                      <CommaSeparatedField
                        value={field.value}
                        onChange={field.onChange}
                        placeholder='e.g. Low, Normal, High'
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
            </Grid>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

RedtailMetricConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default RedtailMetricConfigEditor
